import {
    Button,
    Divider,
    Space,
    Typography,
    Tabs,
    Badge,
    Card,
    List,
    Tag,
    Select,
    Input
} from "antd";
import { Link } from "react-router-dom";
import { DndContext, DragOverlay, useDndMonitor } from "@dnd-kit/core";
import {
    SortableContext,
    arrayMove,
    useSortable,
    verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    PlusOutlined,
    DownloadOutlined,
    PlaySquareOutlined,
    SettingOutlined,
    StarOutlined,
    InboxOutlined,
    DeleteOutlined,
    HolderOutlined,
    WarningOutlined,
    SearchOutlined
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import VideoRow from "../../Components/VideoRow";
import ModuleConfigForm from "../../Components/ModuleConfigForm";
import { getModuleTotalPrice } from "../CollectionCreatorHelper";
import ImportMoreVideos from "../ImportMoreVideos";
import Draggable from "../../Components/Draggable";
import Droppable from "../../Components/Droppable";
import Cupoints from "../../Components/Cupoints";
import ModuleCard from "./ModuleCard";
import { API_URL } from "../../const";
import "../../styles/drag-drop.scss";
import "./AddModulesStep.scss";

const { Text, Title } = Typography;
const { TabPane } = Tabs;

const DragMonitor = ({ setDragMeta }) => {
    const last = useRef({ activeId: null, overId: null });

    const updateMeta = ({ active, over }) => {
        const activeData = active?.data?.current || null;
        const overData = over?.data?.current || null;

        const activeId = active?.id ?? null;
        const overId = over?.id ?? null;

        if (
            last.current.activeId === activeId &&
            last.current.overId === overId
        ) {
            return;
        }

        last.current = { activeId, overId };

        setDragMeta({
            active: activeData,
            over: overData
        });
    };

    useDndMonitor({
        onDragStart: updateMeta,
        onDragMove: updateMeta,
        onDragOver: updateMeta,
        onDragEnd: () => {
            setDragMeta({ active: null, over: null });
            last.current = { activeId: null, overId: null };
        },
        onDragCancel: () => {
            setDragMeta({ active: null, over: null });
            last.current = { activeId: null, overId: null };
        }
    });

    return null;
};

const SortableModuleItem = ({
    module,
    index,
    isSelected,
    hasError,
    onSelect
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        setActivatorNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({
        id: `${module.key}`,
        data: { type: "module-sortable" }
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition: isDragging ? undefined : "transform 120ms ease",
        opacity: isDragging ? 0.6 : undefined
    };

    return (
        <div ref={setNodeRef} style={style}>
            <List.Item
                className={`module-list-item ${isSelected ? "active" : ""} ${isDragging ? "is-dragging" : ""}`}
                onClick={() => onSelect(module.key)}
            >
                <div className="module-list-item-body">
                    <button
                        type="button"
                        className="module-drag-handle"
                        ref={setActivatorNodeRef}
                        onClick={(event) => event.stopPropagation()}
                        {...attributes}
                        {...listeners}
                    >
                        <HolderOutlined />
                    </button>
                    <div>
                        <div className="module-title-row">
                            <Text strong={true}>{module.name || `Module ${index + 1}`}</Text>
                            {hasError && (
                                <WarningOutlined className="module-warning-icon" />
                            )}
                        </div>
                        <div className="module-meta flex flex-col">
                            <Text type="secondary">{module.videos.length} videos</Text>
                            {module.isSuggested && (
                                <Text type="secondary">
                                    By {module.primaryAuthor?.id ? (
                                        <Link target="_blank" to={`/creators/${module.primaryAuthor?.id}`}>
                                            {module.primaryAuthorUsername}
                                        </Link>
                                    ) : (
                                        module.primaryAuthorUsername || "Unknown author"
                                    )}
                                </Text>
                            )}
                        </div>
                    </div>
                </div>
                <Tag color="orange">
                    <Cupoints points={module.price} secondaryWithinBrackets={false} hideDollarAmount={true}></Cupoints>
                </Tag>
            </List.Item>
        </div>
    );
};

const AddModulesStep = ({
    authorsDict,
    collection,
    createdModules,
    currentUser,
    isEditMode,
    goToNextStep,
    goToPreviousStep,
    modules,
    policyModel,
    updateState,
    videoBank,
    addNewSampleModule,
    setAuthorsDict
}) => {
    const [tempVideoBankState, updateVideoBankState] = useState(videoBank);
    const [tempModulesState, updateModuleState] = useState(modules);
    const [dragMeta, setDragMeta] = useState({ active: null, over: null });
    const [selectionMode, setSelectionMode] = useState("suggestions");
    const [selectedModuleKey, setSelectedModuleKey] = useState(null); // modules?.[0]?.key || null
    const [activeTab, setActiveTab] = useState("videos");
    const [suggestionFilter, setSuggestionFilter] = useState("all");
    const [createdModulesSearch, setCreatedModulesSearch] = useState("");
    const [dismissedSuggestionIds, setDismissedSuggestionIds] = useState([]);
    const suggestedModulesExists = useSelector(
        (state) => state.contentCreationReducer.suggestedModulesExists
    );
    const suggestedModules = useSelector(
        (state) => state.contentCreationReducer.suggestedModules
    );
    const collectionCategories = useSelector(
        (state) => state.collectionReducer.collectionCategories
    );

    useEffect(() => {
        if (!isEditMode) return;
        const clonedIds = (tempModulesState || [])
            .map((module) => module.clonedFromModule)
            .filter(Boolean);
        if (clonedIds.length === 0) return;
        setDismissedSuggestionIds((prev) =>
            Array.from(new Set([...prev, ...clonedIds]))
        );
    }, [isEditMode, tempModulesState]);

    useEffect(() => {
        if (!isEditMode) return;
        const needsUpdate = (tempModulesState || []).some(
            (module) => module.clonedFromModule && !module.isSuggested
        );
        if (!needsUpdate) return;
        setNewModulesToState(
            (tempModulesState || []).map((module) =>
                module.clonedFromModule
                    ? { ...module, isSuggested: true }
                    : module
            )
        );
    }, [isEditMode, tempModulesState]);

    const assignableModuleOptions = useMemo(
        () =>
            (tempModulesState || []).map((m, idx) => ({
                key: `${m.key}`,
                name: m.name || `Module ${idx + 1}`
            })),
        [tempModulesState]
    );

    const normalizedSuggestions = useMemo(() => {
        const normalize = (m) => ({
            id: m.id,
            title: m.name,
            videos: m.videos?.length || 0,
            category: m.categoryName || m.category?.name || "Uncategorized",
            categoryId: m.category ?? m.category?.id ?? m.categoryId ?? null,
            videosList: m.videos || [],
            price: Math.ceil(m.price || m.valuePrice || 0),
            authorId: m.primaryAuthor ? m.primaryAuthor.id : null,
            authorName: m.primaryAuthor
                ? `${m.primaryAuthor.firstName || ""} ${m.primaryAuthor.lastName || ""}`.trim() || "Unknown author"
                : "Unknown author"
        });
        return {
            related: (suggestedModules?.list || []).map((m) => normalize(m)),
            shared: (suggestedModules?.others || []).map((m) => normalize(m))
        };
    }, [suggestedModules]);

    const selectableCreatedModules = useMemo(
        () =>
            (createdModules || []).filter(
                (createdModule) =>
                    !tempModulesState.some((module) => module.id === createdModule.id)
            ),
        [createdModules, tempModulesState]
    );

    const normalizedCreatedModules = useMemo(() => {
        const normalize = (m) => ({
            id: m.id,
            title: m.name,
            videos: m.videos?.length || 0,
            category: m.categoryName || m.category?.name || "Uncategorized",
            categoryId: m.category ?? m.category?.id ?? m.categoryId ?? null,
            videosList: m.videos || [],
            price: Math.ceil(m.price || m.valuePrice || m.customPrice || 0),
            authorId: m.primaryAuthor ? m.primaryAuthor.id : null,
            authorName: m.primaryAuthor
                ? `${m.primaryAuthor.firstName || ""} ${m.primaryAuthor.lastName || ""}`.trim() || "Unknown author"
                : "Unknown author",
            sourceModule: m
        });

        return selectableCreatedModules.map((m) => normalize(m));
    }, [selectableCreatedModules]);

    const filteredCreatedModules = useMemo(() => {
        const query = (createdModulesSearch || "").trim().toLowerCase();
        if (!query) return normalizedCreatedModules;
        return normalizedCreatedModules.filter((item) =>
            (item.title || "").toLowerCase().includes(query)
        );
    }, [createdModulesSearch, normalizedCreatedModules]);

    const suggestionFilterMessage = useMemo(() => {
        if (suggestionFilter === "related") {
            return "Showing modules related to the selected category.";
        }
        if (suggestionFilter === "shared") {
            return "Showing modules shared by other authors.";
        }
        return "Showing suggested modules related to your selected category and from other authors.";
    }, [suggestionFilter]);

    const selectedModule = useMemo(
        () => tempModulesState.find((m) => m.key === selectedModuleKey) || null,
        [selectedModuleKey, tempModulesState]
    );

    const selectModule = useCallback((moduleKey, mode = "existing") => {
        setSelectionMode(mode);
        setSelectedModuleKey(moduleKey);
        setActiveTab("videos");
    }, []);

    const SuggestionsEmpty = () => (
        <div className="suggestions-empty">
            <Space direction="vertical" align="center" size="small">
                <InboxOutlined style={{ fontSize: 32, color: "#bfbfbf" }} />
                <Text type="secondary" style={{ textAlign: "center" }}>
                    We couldn&apos;t find suggested modules right now.
                    <br />
                    Import your own or create a new module to continue.
                </Text>
                <Space className="mt-10">
                    <Button
                        icon={<DownloadOutlined />}
                        size={"small"}
                        onClick={() => {
                            setSelectionMode("created");
                            setSelectedModuleKey(null);
                            setActiveTab("videos");
                        }}
                        disabled={createdModules.length === 0}
                    >
                        Import your modules
                    </Button>
                    <Button
                        type="primary"
                        size={"small"}
                        icon={<PlusOutlined />}
                        onClick={addNewModule}
                    >
                        Add new module
                    </Button>
                </Space>
            </Space>
        </div>
    );

    useEffect(() => {
        if (selectionMode === "suggestions" || selectionMode === "created") return;
        if (!tempModulesState.find((m) => m.key === selectedModuleKey)) {
            const fallbackKey = tempModulesState[0]?.key || null;
            selectModule(fallbackKey, fallbackKey ? "existing" : "suggestions");
        }
    }, [selectModule, selectedModuleKey, tempModulesState, selectionMode]);

    const handleOnNextStepClick = () => {
        let collectionValuePrice = 0;
        let collectionNetPrice = 0;
        tempModulesState.forEach((module) => {
            collectionValuePrice += module.price;
            collectionNetPrice += Math.ceil(module.price * (1 - 0.15));
        });
        updateState("collection", {
            ...collection,
            valuePrice: collectionValuePrice,
            collectionNetPrice: collectionNetPrice
        });
        goToNextStep();
    };

    // Re-order within same list
    const reorderWithinList = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result;
    };

    // Set new video bank state
    const setNewVideoBankState = (videos) => {
        updateVideoBankState(videos);
        updateState("videoBank", videos);
    };

    // Set updated modules to state
    const setNewModulesToState = (updatedModules) => {
        updatedModules = calculateNewModulePrices(updatedModules);
        updateModuleState(updatedModules);
        updateState("modules", updatedModules);
    };

    const updateModules = (updater) => {
        const next = updater(Array.from(tempModulesState));
        setNewModulesToState(next);
    };

    // Calculate new prices for modules
    const calculateNewModulePrices = (updatedModules) => {
        updatedModules = updatedModules.map((updatedModule, i) => {
            if (updatedModule.isSuggested) {
                return updatedModule;
            }
            const price = getModuleTotalPrice(updatedModule, policyModel);
            return { ...updatedModule, price };
        });
        return updatedModules;
    };

    // 00. Move modules
    const moveModules = (sourceIndex, destinationIndex) => {
        updateModules((list) => reorderWithinList(list, sourceIndex, destinationIndex));
    };

    // 01. Move video within video bank
    const moveVideoWithinVideoBank = (sourceIndex, destinationIndex) => {
        const reOrderedList = reorderWithinList(
            tempVideoBankState,
            sourceIndex,
            destinationIndex
        );
        setNewVideoBankState(reOrderedList);
    };

    // 02. Move video bank video item into module
    const moveVideoToModule = (moduleKey, sourceIndex, destinationIndex) => {
        const removedVideo = removeVideoFromVideoBank(sourceIndex);
        if (!removedVideo) return;

        updateModules((mods) =>
            mods.map((m) =>
                m.key === moduleKey
                    ? {
                        ...m,
                        videos: [
                            ...m.videos.slice(0, destinationIndex),
                            removedVideo,
                            ...m.videos.slice(destinationIndex)
                        ]
                    }
                    : m
            )
        );
    };

    // 03. Move video within module
    const moveVideoWithinModule = (moduleKey, sourceIndex, destinationIndex) => {
        updateModules((mods) =>
            mods.map((m) =>
                m.key === moduleKey
                    ? {
                        ...m,
                        videos: reorderWithinList(m.videos, sourceIndex, destinationIndex)
                    }
                    : m
            )
        );
    };

    // 04. Move video to video bank from module
    const moveVideoBackToBank = (moduleKey, sourceIndex, destinationIndex) => {
        const bankVideos = Array.from(tempVideoBankState);
        let movedVideo = null;

        const updatedModules = tempModulesState.map((m) => {
            if (m.key !== moduleKey) return m;
            const videos = Array.from(m.videos);
            [movedVideo] = videos.splice(sourceIndex, 1);
            return { ...m, videos };
        });

        if (!movedVideo) return;

        bankVideos.splice(destinationIndex, 0, movedVideo);
        setNewModulesToState(updatedModules);
        setNewVideoBankState(bankVideos);
    };

    // 05. Move video to video bank from module
    const moveVideoModuleToModule = (
        sourceModuleKey,
        destinationModuleKey,
        sourceIndex,
        destinationIndex
    ) => {
        const updatedModules = Array.from(tempModulesState);
        const sourceModuleIndex = updatedModules.findIndex(
            (m) => m.key === sourceModuleKey
        );
        const destinationModuleIndex = updatedModules.findIndex(
            (m) => m.key === destinationModuleKey
        );
        if (sourceModuleIndex < 0 || destinationModuleIndex < 0) return;

        const sourceVideoList = Array.from(updatedModules[sourceModuleIndex].videos);
        const [removedVideo] = sourceVideoList.splice(sourceIndex, 1);
        if (!removedVideo) return;

        const destinationVideoList = Array.from(
            updatedModules[destinationModuleIndex].videos
        );
        destinationVideoList.splice(destinationIndex, 0, removedVideo);

        updatedModules[sourceModuleIndex] = {
            ...updatedModules[sourceModuleIndex],
            videos: sourceVideoList
        };
        updatedModules[destinationModuleIndex] = {
            ...updatedModules[destinationModuleIndex],
            videos: destinationVideoList
        };

        setNewModulesToState(updatedModules);
    };

    // On drag end update state
    const handleOnDragEnd = ({ active, over }) => {
        setDragMeta({ active: null, over: null });

        if (!over) return;

        const activeData = active?.data?.current || {};
        const overData = over?.data?.current || {};

        if (activeData.type === "module-sortable") {
            const activeId = `${active.id}`;
            const overId = `${over.id}`;
            if (activeId === overId) return;
            const sourceIndex = tempModulesState.findIndex(
                (module) => `${module.key}` === activeId
            );
            const destinationIndex = tempModulesState.findIndex(
                (module) => `${module.key}` === overId
            );
            if (sourceIndex < 0 || destinationIndex < 0) return;
            const reordered = arrayMove(
                tempModulesState,
                sourceIndex,
                destinationIndex
            );
            setNewModulesToState(reordered);
            return;
        }

        if (!activeData || !overData) return;

        if (activeData.type !== "video") return;

        const sourceContainer = activeData.container;
        const destinationContainer = overData.container;
        const sourceIndex = activeData.index;
        const destinationIndex = overData.index;

        if (
            sourceContainer == null ||
            destinationContainer == null ||
            sourceIndex == null ||
            destinationIndex == null
        ) {
            return;
        }

        // 01. Video Bank -> Video Bank
        if (
            sourceContainer === "videoBank" &&
            destinationContainer === "videoBank"
        ) {
            if (sourceIndex !== destinationIndex) {
                moveVideoWithinVideoBank(sourceIndex, destinationIndex);
            }
            return;
        }

        // 02. Video Bank -> Module
        if (sourceContainer === "videoBank") {
            moveVideoToModule(destinationContainer, sourceIndex, destinationIndex);
            return;
        }

        // 04. Module -> Video Bank
        if (destinationContainer === "videoBank") {
            moveVideoBackToBank(sourceContainer, sourceIndex, destinationIndex);
            return;
        }

        // 03. Module (X) -> Module (X)
        if (sourceContainer === destinationContainer) {
            if (sourceIndex !== destinationIndex) {
                moveVideoWithinModule(
                    destinationContainer,
                    sourceIndex,
                    destinationIndex
                );
            }
            return;
        }

        // 05. Module (X) -> Module (Y)
        moveVideoModuleToModule(
            sourceContainer,
            destinationContainer,
            sourceIndex,
            destinationIndex
        );
    };

    // Remove module
    const handleRemoveModule = (e, index) => {
        e.stopPropagation();
        const newModules = Array.from(tempModulesState);
        const [removedModule] = newModules.splice(index, 1);
        const videosToRemove = Array.from(removedModule.videos);

        if(!removedModule.isSuggested) {
            setNewVideoBankState([...videosToRemove, ...tempVideoBankState]);
        }

        setNewModulesToState(newModules);
    };

    // Update video title in video bank
    const updateVideoTitleInVideoBank = (newTitle, video, videoIndex) => {
        const newVideosArray = Array.from(tempVideoBankState);
        newVideosArray[videoIndex] = {
            ...video,
            title: newTitle
        };

        setNewVideoBankState(newVideosArray);
    };

    // Update video title in module
    const updateVideoTitleInModule = (
        newTitle,
        module,
        moduleIndex,
        video,
        videoIndex
    ) => {
        const newVideosArray = Array.from(module.videos);
        newVideosArray[videoIndex] = {
            ...video,
            title: newTitle
        };

        const newModulesArray = Array.from(tempModulesState);
        newModulesArray[moduleIndex] = {
            ...module,
            videos: newVideosArray
        };

        setNewModulesToState(newModulesArray);
    };

    // Remove video from video bank
    const removeVideoFromVideoBank = (videoIndex) => {
        const newVideoBankState = Array.from(tempVideoBankState);
        const [removedVideo] = newVideoBankState.splice(videoIndex, 1);

        setNewVideoBankState(newVideoBankState);
        return removedVideo;
    };

    // Scroll to bottom
    const scrollToBottom = () => {
        setTimeout(() => {
            const collectionCreatorModuleListDiv = document.getElementById(
                "collection-creator-modules-list"
            );
            if (collectionCreatorModuleListDiv) {
                collectionCreatorModuleListDiv.scrollTop =
                    collectionCreatorModuleListDiv.scrollHeight;
            }
        }, 100);
    };

    // Add New Module
    const addNewModule = () => {
        const newModules = addNewSampleModule([tempModulesState.length + 1]);
        const updated = [...tempModulesState, ...newModules];
        updateModuleState(updated);
        selectModule(newModules[0]?.key || null, "new");
        setActiveTab("config");
        scrollToBottom();
    };

    // Update Module in state
    const updateModule = (updatedModuleData, updatedModuleIndex) => {
        const shallowModules = Array.from(tempModulesState);
        shallowModules[updatedModuleIndex] = updatedModuleData;
        setNewModulesToState(shallowModules);
    };

    // After import more videos
    const afterImportMoreVideos = (videosToImport) => {
        const newVideoSet = [...videosToImport, ...videoBank];
        setNewVideoBankState(newVideoSet);
    };

    const handleAddSuggestedModule = (item) => {
        const original =
            suggestedModules?.list?.find((m) => m.id === item.id) ||
            suggestedModules?.others?.find((m) => m.id === item.id) ||
            {};

        if (!original?.id) return;

        const newModule = {
            ...original,
            key: `suggestion-${item.id}-${Date.now()}`,
            id: undefined,
            valuePrice: undefined,
            clonedFromModule: original.id,
            isSuggested: true,
            primaryAuthorUsername: original.primaryAuthor?.username,
            coAuthorUsernames: (original.coAuthors || []).map(
                (coAuthor) => coAuthor.username
            )
        };

        const updatedModules = [...tempModulesState, newModule];
        setNewModulesToState(updatedModules);
        setDismissedSuggestionIds((prev) => [...prev, item.id]);
        selectModule(newModule.key, "existing");
    };

    const handleAddCreatedModule = (item) => {
        const original = item?.sourceModule || item;
        if (!original?.id) return;

        const newModule = {
            key: `created-${original.id}-${Date.now()}`,
            isDefaultPrice: original.customPrice === null,
            primaryAuthorUsername: original.primaryAuthor?.username,
            coAuthorUsernames: (original.coAuthors || []).map(
                (coAuthor) => coAuthor.username
            ),
            ...original
        };

        const updatedModules = [...tempModulesState, newModule];
        setNewModulesToState(updatedModules);
    };

    const handleRenameModule = (moduleKey, newName) => {
        if (!moduleKey) return;
        const safeName = (newName || "").trim() || "Untitled Module";
        updateModules((mods) =>
            mods.map((m) =>
                m.key === moduleKey
                    ? {
                        ...m,
                        name: safeName
                    }
                    : m
            )
        );
    };

    const handleViewSuggestedModule = (item) => {
        if (item?.id) {
            window.open(`/modules/${item.id}`, "_blank");
        }
    };

    const handleAssignVideoFromBank = (targetModuleKey, video, sourceIndex) => {
        const moduleIndex = tempModulesState.findIndex(
            (m) => `${m.key}` === `${targetModuleKey}`
        );
        if (moduleIndex < 0) return;

        const bankIndex =
            sourceIndex ??
            tempVideoBankState.findIndex((v) => v.embedId === video.embedId);
        if (bankIndex < 0) return;

        const removedVideo = removeVideoFromVideoBank(bankIndex);

        const updatedModules = Array.from(tempModulesState);
        const targetModule = { ...updatedModules[moduleIndex] };
        targetModule.videos = [...(targetModule.videos || []), removedVideo];
        updatedModules[moduleIndex] = targetModule;
        setNewModulesToState(updatedModules);

        selectModule(targetModule.key, "existing");
    };

    const handleAssignVideoFromModule = (
        targetModuleKey,
        video,
        sourceModuleKey,
        sourceIndex
    ) => {
        if (`${targetModuleKey}` === `${sourceModuleKey}`) return;

        const sourceModuleIndex = tempModulesState.findIndex(
            (m) => `${m.key}` === `${sourceModuleKey}`
        );
        const destinationModuleIndex = tempModulesState.findIndex(
            (m) => `${m.key}` === `${targetModuleKey}`
        );
        if (sourceModuleIndex < 0 || destinationModuleIndex < 0) return;

        const updatedModules = Array.from(tempModulesState);

        // remove from source
        const sourceModule = { ...updatedModules[sourceModuleIndex] };
        const sourceVideos = Array.from(sourceModule.videos || []);
        const [removedVideo] = sourceVideos.splice(
            sourceIndex ??
            sourceVideos.findIndex((v) => v.embedId === video.embedId),
            1
        );
        sourceModule.videos = sourceVideos;
        updatedModules[sourceModuleIndex] = sourceModule;

        if (!removedVideo) return;

        // add to destination
        const destinationModule = { ...updatedModules[destinationModuleIndex] };
        destinationModule.videos = [
            ...(destinationModule.videos || []),
            removedVideo
        ];
        updatedModules[destinationModuleIndex] = destinationModule;

        setNewModulesToState(updatedModules);
        selectModule(sourceModuleKey, "existing");
    };

    const fetchCreatorList = async (username) => {
        return fetch(
            API_URL +
            `/api/v1/creators/${username ? "?search=" + username : ""}`,
            {
                method: "GET",
                headers: {
                    Authorization: "Bearer " + localStorage.getItem("access"),
                    "Content-Type": "application/json"
                }
            }
        )
            .then((response) => response.json())
            .then((body) => {
                const options = [];
                const authors = {};
                body.forEach((user) => {
                    authors[user.username] = user;
                    options.push({
                        label: `${user.firstName} ${user.lastName}`,
                        value: user.username
                    });
                });

                setAuthorsDict({
                    ...authorsDict,
                    ...authors
                });
                return options;
            });
    };

    const getPlaceholderIndex = useCallback(
        (containerKey, length) => {
            const over = dragMeta.over;
            if (!over || over.container !== containerKey) return null;
            const overIndex = over.index ?? length;
            return Math.max(0, Math.min(length, overIndex));
        },
        [dragMeta.over]
    );

    const renderPlaceholder = (key, style = {}) => (
        <div
            key={key}
            className="droppable-placeholder-manual drag-placeholder"
            style={style}
        />
    );

    const isActiveItem = useCallback(
        (container, index) =>
            dragMeta.active?.container === container &&
            dragMeta.active?.index === index,
        [dragMeta.active]
    );

    const getActiveItem = useCallback(() => {
        const active = dragMeta.active;
        if (!active || active.index == null || !active.container) return null;
        if (active.container === "videoBank") {
            return (
                tempVideoBankState[active.index] ||
                active.video ||
                tempVideoBankState.find((v) => v.embedId === active.embedId) ||
                null
            );
        }
        const module = tempModulesState.find(
            (m) => m.key === active.container
        );
        return (
            module?.videos?.[active.index] ||
            active.video ||
            module?.videos?.find((v) => v.embedId === active.embedId) ||
            null
        );
    }, [dragMeta.active, tempModulesState, tempVideoBankState]);

    const videoBankPlaceholderIndex = useMemo(
        () => getPlaceholderIndex("videoBank", tempVideoBankState.length),
        [getPlaceholderIndex, tempVideoBankState.length]
    );

    const renderModuleVideosTab = () => {
        if (!selectedModule) return <Text type="secondary">Select a module to manage its videos.</Text>;
        const moduleIndex = tempModulesState.findIndex((m) => m.key === selectedModule.key);
        const moduleVideosPlaceholderIndex = getPlaceholderIndex(
            selectedModule.key,
            selectedModule.videos.length
        );

        return (
            <div className="videos-tab custom-scrollbar-y">
                <div className="videos-in-module-list-container">
                    <Droppable
                        id={`moduleVideos-${selectedModule.key}`}
                        data={{
                            type: "video-container",
                            container: selectedModule.key,
                            index: selectedModule.videos.length,
                            isContainer: true
                        }}
                        isDisabled={selectedModule?.isSuggested}
                        layout="grid"
                        showPlaceholder={false}
                        gridColumns={4}>
                        <div
                            className={`collection-creator-videos-in-module-list custom-scrollbar-x drag-list-grid drag-area-full ${selectedModule
                                .videos
                                .length ===
                                0
                                ? "no-video"
                                : ""
                                } ${moduleVideosPlaceholderIndex !== null &&
                                    selectedModule
                                        .videos
                                        .length === 0
                                    ? "is-dragging"
                                    : ""
                                }`}>
                            {selectedModule.videos.map(
                                (
                                    video,
                                    videoIndex
                                ) => {
                                    return (
                                        <React.Fragment key={`module-${selectedModule.key}-video-${video.embedId}`}>
                                            {moduleVideosPlaceholderIndex ===
                                                videoIndex &&
                                                renderPlaceholder(
                                                    `module-${selectedModule.key}-before-${video.embedId}`
                                                )}
                                            <Draggable
                                                id={`module-${selectedModule.key}-video-${video.embedId}`}
                                                data={{
                                                    type: "video",
                                                    container: selectedModule.key,
                                                    index: videoIndex,
                                                    video,
                                                    embedId: video.embedId
                                                }}
                                                isDraggable={
                                                    !!selectedModule &&
                                                    !selectedModule?.isSuggested
                                                }
                                                droppableData={{
                                                    type: "video",
                                                    container: selectedModule.key,
                                                    index: videoIndex,
                                                    embedId: video.embedId
                                                }}>
                                                <div
                                                    className={`draggable-video drag-draggable ${isActiveItem(
                                                        selectedModule.key,
                                                        videoIndex
                                                    )
                                                        ? "drag-source-hidden"
                                                        : ""
                                                        }`}>
                                                    <VideoRow
                                                        video={
                                                            video
                                                        }
                                                        isDisabled={!!selectedModule?.isSuggested}
                                                        assignableModules={assignableModuleOptions.filter(
                                                            (m) =>
                                                                `${m.key}` !== `${selectedModule.key}` &&
                                                                !tempModulesState.find(
                                                                    (mod) =>
                                                                        `${mod.key}` === `${m.key}` &&
                                                                        mod.isSuggested
                                                                )
                                                        )}
                                                        onAssignToModule={(moduleKey) =>
                                                            handleAssignVideoFromModule(
                                                                moduleKey,
                                                                video,
                                                                selectedModule.key,
                                                                videoIndex
                                                            )
                                                        }
                                                        removeable={!selectedModule?.isSuggested}
                                                        removeFromVideoBank={
                                                            selectedModule?.isSuggested
                                                                ? undefined
                                                                : () =>
                                                                    moveVideoBackToBank(
                                                                        selectedModule.key,
                                                                        videoIndex,
                                                                        tempVideoBankState.length
                                                                    )
                                                        }
                                                        updateTempState={(
                                                            newTitle
                                                        ) => {
                                                            if (!selectedModule?.isSuggested) {
                                                                updateVideoTitleInModule(
                                                                    newTitle,
                                                                    selectedModule,
                                                                    moduleIndex,
                                                                    video,
                                                                    videoIndex
                                                                );
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </Draggable>
                                        </React.Fragment>
                                    );
                                }
                            )}
                            {moduleVideosPlaceholderIndex ===
                                selectedModule.videos.length &&
                                renderPlaceholder(
                                    `module-${selectedModule.key}-after`
                                )}
                        </div>
                    </Droppable>
                </div>
            </div>
        );
    };

    const hasModuleError = useCallback(
        (module) =>
            !module ||
            !(module.name || "").trim(),
        []
    );

    const renderModuleConfigTab = () => {
        if (!selectedModule) return <Text type="secondary">Select a module to configure.</Text>;
        const moduleIndex = tempModulesState.findIndex((m) => m.key === selectedModule.key);
        return (
            <Card size="small" className="module-config-card custom-scrollbar-y">
                <ModuleConfigForm
                    module={selectedModule}
                    moduleIdentity={selectedModule.key}
                    editPermission={selectedModule?.isSuggested ? "NameOnly" : "Default"}
                    currentUser={currentUser}
                    policyModel={policyModel}
                    authorsDict={authorsDict}
                    collectionCategories={collectionCategories}
                    fetchCreatorList={fetchCreatorList}
                    autoSave={true}
                    onModuleChange={(updatedModule) =>
                        updateModule(updatedModule, moduleIndex)
                    }
                    showActions={false}
                />
            </Card>
        );
    };

    const renderActions = () => (
        <Space direction="vertical" className="module-actions" size="small">
            <Button
                className="action-btn"
                type={selectionMode === "suggestions" ? "primary" : "default"}
                icon={<StarOutlined />}
                block
                onClick={() => {
                    setSelectionMode("suggestions");
                    setSelectedModuleKey(null);
                    setActiveTab("videos");
                }}>
                Our Suggestions
            </Button>

            {createdModules.length > 0 && (
                <>
                    <Button
                        type={selectionMode === "created" ? "primary" : "default"}
                        icon={<DownloadOutlined />}
                        block
                        onClick={() => {
                            setSelectionMode("created");
                            setSelectedModuleKey(null);
                            setActiveTab("videos");
                        }}>
                        Import Your Modules
                    </Button>
                </>
            )}
        </Space>
    );

    const renderLeftColumn = () => {
        if (selectionMode === "suggestions") {
            if (!suggestedModulesExists) {
                return (
                    <Card size="small">
                        <Text type="secondary">No suggested modules available.</Text>
                    </Card>
                );
            }

            const relatedModules = normalizedSuggestions.related.filter(
                (item) => !dismissedSuggestionIds.includes(item.id)
            );
            const sharedModules = normalizedSuggestions.shared.filter(
                (item) => !dismissedSuggestionIds.includes(item.id)
            );

            const renderSuggestionList = (items) => (
                <div className="suggestions-list">
                    <Space direction="vertical" size={0} style={{ width: "100%" }}>
                        {items
                            .filter((item) => !dismissedSuggestionIds.includes(item.id))
                            .map((item) => (
                                <ModuleCard
                                    key={item.id}
                                    item={item}
                                    onAddToCourse={() => handleAddSuggestedModule(item)}
                                />
                            ))}
                    </Space>
                </div>
            );

            return (
                <>
                    <div className="flex suggestions-controls">
                        <Space direction="vertical" size={0} className="mb-10">
                            <Title level={5} style={{ marginBottom: 0 }}>
                                Our Suggestions
                            </Title>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {suggestionFilterMessage}
                            </Text>
                        </Space>
                        <Space className="ml-auto" align="start" size={8}>
                            <Select
                                size="small"
                                bordered={false}

                                value={suggestionFilter}
                                onChange={setSuggestionFilter}
                                options={[
                                    { label: "All", value: "all" },
                                    { label: "Related Modules", value: "related" },
                                    { label: "Others", value: "shared" }
                                ]}
                                style={{ minWidth: 140, textAlign: "right" }}
                            />
                        </Space>

                    </div>

                    <div className="suggestions-lists-container custom-scrollbar-y pr-5">
                        {suggestionFilter === "all" && (
                            <>
                                {relatedModules.length > 0 && renderSuggestionList(relatedModules)}
                                {sharedModules.length > 0 && renderSuggestionList(sharedModules)}
                                {relatedModules.length === 0 && sharedModules.length === 0 && (
                                    <SuggestionsEmpty />
                                )}
                            </>
                        )}
                        {suggestionFilter === "related" && (
                            relatedModules.length > 0 ? (
                                renderSuggestionList(relatedModules)
                            ) : (
                                <SuggestionsEmpty />
                            )
                        )}
                        {suggestionFilter === "shared" && (
                            sharedModules.length > 0 ? (
                                renderSuggestionList(sharedModules)
                            ) : (
                                <SuggestionsEmpty />
                            )
                        )}
                    </div>
                </>

            );
        }

        if (selectionMode === "created") {
            if (normalizedCreatedModules.length === 0) {
                return (
                    <Card size="small">
                        <Text type="secondary">No created modules available.</Text>
                    </Card>
                );
            }

            return (
                <>
                    <div className="flex suggestions-controls">
                        <Space direction="vertical" size={0} className="mb-10">
                            <Title level={5} style={{ marginBottom: 0 }}>
                                Your Modules{" "}
                                <Text type="secondary" style={{ fontSize: 10, fontWeight: "normal" }}>
                                    {createdModulesSearch.trim()
                                        ? `(Showing ${filteredCreatedModules.length} of ${normalizedCreatedModules.length} modules.)`
                                        : `(${String(normalizedCreatedModules.length).padStart(2, "0")})`}
                                </Text>
                            </Title>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Browse your created modules and add them to this course.
                            </Text>
                        </Space>
                        <Space className="ml-auto" align="start" size={8}>
                            <Input
                                size="middle"
                                placeholder="Search modules"
                                prefix={<SearchOutlined />}
                                value={createdModulesSearch}
                                onChange={(event) =>
                                    setCreatedModulesSearch(event.target.value)
                                }
                                allowClear
                                style={{ width: 200 }}
                            />
                        </Space>
                    </div>

                    <div className="suggestions-lists-container custom-scrollbar-y pr-5">
                        <div className="suggestions-list">
                            <Space direction="vertical" size={0} style={{ width: "100%" }}>
                                {filteredCreatedModules.length > 0 ? (
                                    filteredCreatedModules.map((item) => (
                                        <ModuleCard
                                            key={item.id}
                                            item={item}
                                            isOwnModule={true}
                                            onAddToCourse={handleAddCreatedModule}
                                        />
                                    ))
                                ) : (
                                    <Card size="small">
                                        <Text type="secondary">
                                            No modules match your search.
                                        </Text>
                                    </Card>
                                )}
                            </Space>
                        </div>
                    </div>
                </>
            );
        }

        return (
            <div className="selected-module-panel">
                <div className="selected-module-header">
                    <div>
                        <Text type="secondary">Selected module</Text>
                        {selectedModule ? (
                            <Title
                                level={4}
                                style={{ margin: 0, width: "100%" }}
                                editable={{
                                    autoSize: true,
                                    onChange: (value) =>
                                        handleRenameModule(selectedModule.key, value)
                                }}>
                                {selectedModule?.name || "Untitled Module"}
                            </Title>
                        ) : (
                            <Title level={4} style={{ margin: 0 }}>
                                No module selected
                            </Title>
                        )}
                    </div>
                    {selectedModule && tempModulesState.length > 0 && (
                        <Button
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            className="selected-module-delete"
                            onClick={(event) => {
                                const moduleIndex = tempModulesState.findIndex(
                                    (m) => m.key === selectedModule.key
                                );
                                if (moduleIndex < 0) return;
                                handleRemoveModule(event, moduleIndex);
                            }}
                        >
                            Delete
                        </Button>
                    )}
                </div>
                <Tabs
                    defaultActiveKey={activeTab}
                    onChange={setActiveTab}
                >
                    <TabPane
                        tab={
                            <Space size={0}>
                                <span>
                                    <PlaySquareOutlined />
                                    Videos
                                </span>
                                <Badge className="ml-5" size={"default"} count={selectedModule?.videos?.length}>
                                </Badge>
                            </Space>
                        }
                        key="videos"
                        className="videos-tab">
                        {renderModuleVideosTab()}
                    </TabPane>
                    <TabPane
                        tab={
                            <Space size={0}>
                                <span>
                                    <SettingOutlined />
                                    Configurations
                                </span>
                                {selectedModule && hasModuleError(selectedModule) && (
                                    <WarningOutlined className="module-warning-icon" />
                                )}
                            </Space>
                        }
                        key="config"
                        className="config-tab">
                        {renderModuleConfigTab()}
                    </TabPane>
                </Tabs>
            </div >
        );
    };

    const renderModuleList = () => (
        <div className="module-list">
            <Title level={5} className="flex space-between align-center mb-12 w-100">
                <span>Modules&nbsp;
                    <Text type="secondary" style={{ fontSize: 10, fontWeight: "normal" }}>
                        ({String(tempModulesState.length).padStart(2, "0")})
                    </Text>
                </span>
                <Button
                    // type={selectionMode === "new" ? "primary" : "default"}
                    type={"primary"}
                    icon={<PlusOutlined />}
                    onClick={addNewModule}>
                    Add New Module
                </Button>
            </Title>

            <div className="module-list-container custom-scrollbar-y pr-5">
                {tempModulesState.length === 0 ? (
                    <Text type="secondary">No modules yet. Add a new one to get started.</Text>
                ) : (
                    <List
                        className="flex flex-col"
                        dataSource={tempModulesState}
                        renderItem={(module, index) => (
                            <SortableModuleItem
                                key={module.key}
                                module={module}
                                index={index}
                                isSelected={selectedModuleKey === module.key}
                                hasError={hasModuleError(module)}
                                onSelect={(moduleKey) => {
                                    setSelectionMode("existing");
                                    setSelectedModuleKey(moduleKey);
                                    setActiveTab("videos");
                                }}
                            />
                        )}
                    />
                )}
            </div>
        </div>
    );

    return (
        <div>
            <DndContext onDragEnd={handleOnDragEnd}>
                <DragMonitor setDragMeta={setDragMeta} />
                <div className="new-module-container">
                    {/* Video Bank Section :: Start */}
                    <div className="video-bank-container">
                        <Space direction="vertical">
                            <div className="video-bank-header">
                                <Title level={4} style={{ margin: 0 }}>
                                    Video Bank
                                    <Text type="secondary" className="ml-5" italic style={{ fontSize: "12px" }}>
                                        {tempVideoBankState.length > 0 && (
                                            <>
                                                ({tempVideoBankState.length} video{tempVideoBankState.length > 1 ? 's' : ''})
                                            </>
                                        )}
                                    </Text>
                                </Title>
                                <div className="mt-10">
                                    <ImportMoreVideos
                                        modules={tempModulesState}
                                        videoBank={videoBank}
                                        afterImport={afterImportMoreVideos}
                                    />
                                </div>
                            </div>
                            <div className="video-bank">
                                {tempVideoBankState.length === 0 ? (
                                    <div className="no-videos-in-bank">
                                        <PlaySquareOutlined />
                                        <Text type="secondary">
                                            Your video bank is empty. <br />
                                            Add videos from YouTube or Vimeo above.
                                        </Text>
                                    </div>
                                ) : (
                                    <Droppable
                                        id="videoBank-container"
                                        data={{
                                            type: "video-container",
                                            container: "videoBank",
                                            index: tempVideoBankState.length,
                                            isContainer: true
                                        }}
                                        layout="vertical"
                                        showPlaceholder={false}>
                                        <div className="video-bank-list custom-scrollbar-y drag-list-vertical drag-area-full list-reset">
                                            {tempVideoBankState.map(
                                                (video, videoIndex) => {
                                                    return (
                                                        <div key={`videoBank-${video.embedId}`}>
                                                            {videoBankPlaceholderIndex ===
                                                                videoIndex &&
                                                                renderPlaceholder(
                                                                    `videoBank-before-${video.embedId}`
                                                                )}
                                                            <Draggable
                                                                id={`videoBank-${video.embedId}`}
                                                                data={{
                                                                    type: "video",
                                                                    container: "videoBank",
                                                                    index: videoIndex,
                                                                    video,
                                                                    embedId: video.embedId
                                                                }}
                                                                isDraggable={
                                                                    (selectionMode === "existing" || selectionMode === "new") &&
                                                                    !selectedModule?.isSuggested
                                                                }
                                                                droppableData={{
                                                                    type: "video",
                                                                    container: "videoBank",
                                                                    index: videoIndex,
                                                                    embedId: video.embedId
                                                                }}>
                                                                <li
                                                                    className={`no-list-style drag-draggable ${isActiveItem(
                                                                        "videoBank",
                                                                        videoIndex
                                                                    )
                                                                        ? "drag-source-hidden"
                                                                        : ""
                                                                        }`}>
                                                                    <VideoRow
                                                                        index={
                                                                            videoIndex
                                                                        }
                                                                        video={
                                                                            video
                                                                        }
                                                                        isDisabled={false}
                                                                        assignableModules={assignableModuleOptions.filter(
                                                                            (m) => !tempModulesState.find((mod) => `${mod.key}` === `${m.key}` && mod.isSuggested)
                                                                        )}
                                                                        onAssignToModule={(moduleKey) =>
                                                                            handleAssignVideoFromBank(
                                                                                moduleKey,
                                                                                video,
                                                                                videoIndex
                                                                            )
                                                                        }
                                                                        removeable={
                                                                            true
                                                                        }
                                                                        removeFromVideoBank={
                                                                            removeVideoFromVideoBank
                                                                        }
                                                                        updateTempState={(
                                                                            newTitle
                                                                        ) => {
                                                                            updateVideoTitleInVideoBank(
                                                                                newTitle,
                                                                                video,
                                                                                videoIndex
                                                                            );
                                                                        }}
                                                                    />
                                                                </li>
                                                            </Draggable>
                                                        </div>
                                                    );
                                                }
                                            )}
                                            {videoBankPlaceholderIndex ===
                                                tempVideoBankState.length &&
                                                renderPlaceholder("videoBank-after")}
                                        </div>
                                    </Droppable>
                                )}
                            </div>
                        </Space>
                    </div>
                    {/* Video Bank Section :: End */}

                    {/* Module List Section :: Start */}
                    <div className="module-layout">
                        <div
                            key={`left-${selectionMode}-${selectedModuleKey || "none"}`}
                            className={`module-layout__left fade-swap`}>
                            {renderLeftColumn()}
                        </div>
                        <div className="module-layout__right">
                            {renderActions()}
                            <SortableContext
                                items={tempModulesState.map((module) => `${module.key}`)}
                                strategy={verticalListSortingStrategy}
                            >
                                {renderModuleList()}
                            </SortableContext>
                        </div>
                    </div>
                </div>
                <DragOverlay>
                    {(() => {
                        const activeItem = getActiveItem();
                        if (!activeItem) return null;
                        return (
                            <div className="drag-overlay-card">
                                <VideoRow video={activeItem} />
                            </div>
                        );
                    })()}
                </DragOverlay>
            </DndContext>
            <Divider></Divider>
            <Space>
                <Button onClick={goToPreviousStep}>Back</Button>
                <Button
                    type="primary"
                    onClick={handleOnNextStepClick}
                    disabled={
                        tempModulesState.length < 1 ||
                        tempModulesState.some((t) => t.videos.length === 0) ||
                        tempModulesState.some((t) => !(t.name || "").trim())
                    }>
                    Next
                </Button>
            </Space>
        </div>
    );
};

export default AddModulesStep;
